import React, { useMemo } from "react";
import { Table } from "antd";
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../../services/firebaseService/connection"; // adjust path
import DescriptionText from "../../../../../CommonComponents/DescriptionText";
import useMembersData from "../../Members/useMembersData"; // adjust path
import "./FlaggedMembers.css";

const fetchBlockedUsersData = async () => {
  const snapshot = await getDocs(collection(db, "blockedUsers"));

  const aggregated = {};

  snapshot.forEach((doc) => {
    const blockerId = doc.id;
    const { blockedUserIds = [] } = doc.data();

    blockedUserIds.forEach((blockedId) => {
      if (!aggregated[blockedId]) {
        aggregated[blockedId] = { count: 0, flaggedBy: [] };
      }
      aggregated[blockedId].count += 1;
      aggregated[blockedId].flaggedBy.push(blockerId);
    });
  });

  return Object.entries(aggregated)
    .map(([userId, data]) => ({ userId, ...data }))
    .sort((a, b) => b.count - a.count);
};

const FlaggedMembers = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["flaggedMembers"],
    queryFn: fetchBlockedUsersData,
  });

  const { getMemberById, membersLoading, membersList } = useMembersData();

  const memberIdSet = useMemo(
    () => new Set((membersList || []).map((m) => m?.id).filter(Boolean)),
    [membersList],
  );

  const tableData = useMemo(
    () =>
      (data || []).reduce((acc, { userId, flaggedBy }) => {
        if (!memberIdSet.has(userId)) return acc;

        const validFlaggedBy = flaggedBy.filter((id) => memberIdSet.has(id));
        if (validFlaggedBy.length === 0) return acc;

        acc.push({
          key: userId,
          blocked: getMemberById(userId)?.name || userId,
          count: validFlaggedBy.length,
          flaggedBy: validFlaggedBy
            .map((id) => getMemberById(id)?.name || id)
            .join(", "),
        });

        return acc;
      }, []),
    [data, memberIdSet, getMemberById],
  );

  const columns = [
    {
      title: "Blocked",
      dataIndex: "blocked",
      key: "blocked",
    },
    // {
    //   title: "Type",
    //   dataIndex: "type",
    //   key: "type",
    // },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      sorter: (a, b) => b.count - a.count,
    },
    {
      title: "Flagged By",
      dataIndex: "flaggedBy",
      key: "flaggedBy",
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <DescriptionText showBackground>
        Review and manage members who have been blocked by others. To address
        recurring safety concerns, you can restrict app access by selecting
        &quot;Lock/Archive&quot; in the Member&apos;s profile.
      </DescriptionText>

      {isError ? (
        <p style={{ color: "#e74c3c", fontSize: "14px", margin: 0 }}>
          Failed to load flagged members.
        </p>
      ) : (
        <Table
          bordered
          columns={columns}
          dataSource={tableData}
          loading={isLoading || membersLoading}
          rowKey="key"
          scroll={{ x: "max-content" }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            position: ["bottomLeft"],
          }}
          bodyStyle={{
            fontSize: window.screen.width > 500 ? "18px" : "12px",
            backgroundColor: "#ffffff",
          }}
        />
      )}
    </div>
  );
};

export default FlaggedMembers;
